import { t, type StaffKey, type StaffLanguage } from './staffText'
import type { Branch, Webhook } from './types'

/**
 * The pure logic behind the Webhooks section: what may be chosen, what is wrong
 * with an endpoint as typed, and how its branches are said. Every check mirrors
 * internal/webhook.Problems, so the form says it first and in the reader's
 * language; the server remains the authority, and also checks the address against
 * the installation's outbound guard, which only it knows.
 */

/** Every event an endpoint may ask for, in internal/webhook.Offered's order. */
export const WEBHOOK_EVENTS = [
  'ticket.issued',
  'ticket.called',
  'ticket.recalled',
  'ticket.serving',
  'ticket.transaction_closed',
  'ticket.no_show',
  'ticket.closed',
  'ticket.voided',
  'ticket.left',
] as const
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number]

export const EVENT_LABEL: Record<WebhookEvent, StaffKey> = {
  'ticket.issued': 'webhooks.event.ticket.issued',
  'ticket.called': 'webhooks.event.ticket.called',
  'ticket.recalled': 'webhooks.event.ticket.recalled',
  'ticket.serving': 'webhooks.event.ticket.serving',
  'ticket.transaction_closed': 'webhooks.event.ticket.transaction_closed',
  'ticket.no_show': 'webhooks.event.ticket.no_show',
  'ticket.closed': 'webhooks.event.ticket.closed',
  'ticket.voided': 'webhooks.event.ticket.voided',
  'ticket.left': 'webhooks.event.ticket.left',
}

export interface WebhookDraft {
  name: string
  url: string
  events: WebhookEvent[]
  branchIds: string[]
  active: boolean
}

/** A new endpoint starts with the five events a CRM asks for first. */
export function emptyDraft(): WebhookDraft {
  return {
    name: '',
    url: '',
    events: ['ticket.issued', 'ticket.called', 'ticket.serving', 'ticket.no_show', 'ticket.closed'],
    branchIds: [],
    active: true,
  }
}

export function draftOf(w: Webhook): WebhookDraft {
  return {
    name: w.name,
    url: w.url,
    events: w.events.filter((e): e is WebhookEvent => (WEBHOOK_EVENTS as readonly string[]).includes(e)),
    branchIds: [...w.branchIds],
    active: w.active,
  }
}

function parsed(url: string): URL | null {
  try {
    return new URL(url.trim())
  } catch {
    return null
  }
}

/** internal/webhook.Problems' limits: a name in runes, an address in bytes. */
export const MAX_NAME_RUNES = 80
export const MAX_URL_BYTES = 2048

export function webhookProblems(d: WebhookDraft, lang: StaffLanguage): string[] {
  const out: string[] = []
  const name = d.name.trim()
  if (!name) out.push(t('webhooks.problem.name', lang))
  // Counted by code point, as Go counts runes, so 'ə' is one and an emoji is one.
  else if ([...name].length > MAX_NAME_RUNES) out.push(t('webhooks.problem.nameLong', lang))
  const u = parsed(d.url)
  if (!u || (u.protocol !== 'https:' && u.protocol !== 'http:') || !u.hostname) {
    out.push(t('webhooks.problem.url', lang))
  } else if (new TextEncoder().encode(d.url.trim()).length > MAX_URL_BYTES) {
    out.push(t('webhooks.problem.urlLong', lang))
  } else if (u.username || u.password) {
    out.push(t('webhooks.problem.password', lang))
  } else if (u.hash || d.url.trim().endsWith('#')) {
    out.push(t('webhooks.problem.fragment', lang))
  }
  if (d.events.length === 0) out.push(t('webhooks.problem.events', lang))
  return out
}

/** Whether the address is plain http, which the form warns about but allows. */
export function isPlainHttp(url: string): boolean {
  return parsed(url)?.protocol === 'http:'
}

export function branchSummary(ids: readonly string[], branches: readonly Branch[], lang: StaffLanguage): string {
  if (ids.length === 0) return t('webhooks.everyBranch', lang)
  if (ids.length === 1) return branches.find((b) => b.id === ids[0])?.name ?? t('webhooks.branchCount', lang, { n: 1 })
  return t('webhooks.branchCount', lang, { n: ids.length })
}
